// Lead-sheet lines (Oct 2026, from a band rehearsal: "a score with 4 bars per
// line"): how many full bars each line of a lead sheet holds, where a line
// must start, and how small the notation may draw so the lines fit the page.
//
// The conventions, measured on the OpenBook sources in corpus/ and checked
// against the engraving guides (Argue's Music Preparation Fundamentals, the
// Sibelius reference):
//  - four bars a line is the jazz norm;
//  - a pickup sits ahead of the first line's bars and is not counted (every
//    OpenBook pickup tune puts it before four full bars);
//  - a named section starts a new line (319 of OpenBook's 322 section marks);
//  - a later ending starts its own line, holding what is left of the line its
//    first ending sat on, so the music after it starts a new line again;
//  - a coda starts a new line.
// A line too wide for the page shrinks the whole staff, one size for the tune
// set by the widest line, down to a floor; past the floor a line splits in
// half (four bars to two and two, never three and one), so a phrase never
// straddles a line break. Guitar Pro takes exactly N bars and squeezes them;
// readers like Flat.io and OSMD treat N as a ceiling. Shrinking first keeps the
// phrase grid where the size allows it. The fit is made at the default size and
// the reader's size scales it, so the size slider always changes the size.

import type { Section } from '@/music/song';

/** How many full bars a lead-sheet line holds; 'auto' fills the width (up to four). */
export type BarsPerLine = 'auto' | 2 | 3 | 4 | 6 | 8;

export const BARS_PER_LINE: readonly BarsPerLine[] = ['auto', 2, 3, 4, 6, 8];

export const DEFAULT_BARS_PER_LINE: BarsPerLine = 4;

export function isBarsPerLine(value: unknown): value is BarsPerLine {
  return BARS_PER_LINE.includes(value as BarsPerLine);
}

/** A persisted or commanded value: anything unknown reads as the default. */
export function normalizeBarsPerLine(value: unknown): BarsPerLine {
  return isBarsPerLine(value) ? value : DEFAULT_BARS_PER_LINE;
}

/**
 * The smallest the notation draws at the default size (100%) to fit a line, as
 * a share of that size: about a printed lead sheet's staff on a tablet. Past
 * this much shrinking a line splits instead.
 */
export const LINE_FIT_FLOOR = 0.75;

/** A system's width bounds, in staff spaces. */
export const SYSTEM_WIDTH_MIN = 24;
export const SYSTEM_WIDTH_MAX = 180;

/**
 * The page's width model. At `s` px per staff space a system is
 * `px / s − spaces` staff spaces wide, within the bounds above: `px` is the
 * surface's width less any fixed-px gutters, `spaces` the margins, clef and
 * key signature, which scale with the staff.
 */
export interface SystemGeometry {
  px: number;
  spaces: number;
}

export function systemWidthAt(geometry: SystemGeometry, spaceSize: number): number {
  return Math.max(
    SYSTEM_WIDTH_MIN,
    Math.min(SYSTEM_WIDTH_MAX, geometry.px / spaceSize - geometry.spaces),
  );
}

/** What a line plan reads off each bar. */
export interface LineBar {
  /** The opening anacrusis: on the first line, not counted. */
  pickup: boolean;
  /** A line starts here: a named section, a later ending, a coda. */
  lineStart: boolean;
  /** A first ending opens here. */
  firstEnding: boolean;
  /** A later ending (2, 3, …) opens here. */
  laterEnding: boolean;
}

// A D.C. or D.S. ends the main body: what follows it is the coda (or a tag).
const NAVIGATION_JUMP = /\bD\.\s*[CS]\./i;
const TO_CODA = /\bto\s+coda\b/i;

/**
 * Each bar's part in the line plan. Sections are the song's named sections
 * ('Main' is the name a song gives the bars before its first letter, never a
 * section start). A coda starts where a second coda sign follows a To Coda,
 * or straight after a D.C./D.S. — the chart marks the jump and the target
 * with the same sign.
 */
export function lineBars(
  sections: readonly Section[],
  timeSignature: readonly [number, number],
): LineBar[] {
  const bars = sections.flatMap((section) => section.bars);
  const sectionStarts = new Set<number>();
  let index = 0;
  for (const section of sections) {
    if (section.name && section.name !== 'Main' && section.bars.length > 0)
      sectionStarts.add(index);
    index += section.bars.length;
  }
  const first = bars[0];
  const pickup = !!first && first.beats < (first.timeSig ?? timeSignature)[0] - 1e-6;
  let codaSignPassed = false;
  return bars.map((bar, i) => {
    const previous = bars[i - 1];
    const laterEnding = (bar.ending ?? 0) >= 2;
    const codaStart =
      (!!bar.coda && codaSignPassed) ||
      (!!previous?.directive && NAVIGATION_JUMP.test(previous.directive));
    if (bar.coda || (bar.directive && TO_CODA.test(bar.directive))) codaSignPassed = true;
    // The pickup leads into the first section: the two share a line.
    const sectionStart = sectionStarts.has(i) && !(i === 1 && pickup);
    return {
      pickup: i === 0 && pickup,
      lineStart: i > 0 && (sectionStart || laterEnding || codaStart),
      firstEnding: bar.ending === 1,
      laterEnding,
    };
  });
}

/**
 * One line of a plan: bars [start, end), and the share of the system width
 * it fills — 1 for a full line, less for one the music leaves short (a
 * section that ends early, a later ending, the last line), so its bars keep
 * the grid's width.
 */
export interface PlannedLine {
  start: number;
  end: number;
  fill: number;
}

/** Lines of `perLine` full bars, starting a new line wherever a bar asks. */
export function planLines(bars: readonly LineBar[], perLine: number): PlannedLine[] {
  const per = Math.max(1, Math.floor(perLine));
  const lines: PlannedLine[] = [];
  let start = 0;
  let count = 0; // full bars on the current line
  let capacity = per; // full bars the current line holds
  let firstEndingAt = 0; // full bars before the latest first ending, on its line
  const close = (end: number) => {
    if (end > start) lines.push({ start, end, fill: Math.min(1, count / per) });
    start = end;
    count = 0;
    capacity = per;
  };
  bars.forEach((bar, i) => {
    if (i > start && (bar.lineStart || (count >= capacity && !bar.pickup))) close(i);
    // A later ending sits where its first ending did: its line holds only
    // what was left of that line, and the music after it starts afresh.
    if (bar.laterEnding && firstEndingAt > 0) capacity = per - firstEndingAt;
    if (bar.firstEnding) firstEndingAt = count;
    if (!bar.pickup) count += 1;
  });
  close(bars.length);
  return lines;
}

/** How a plan is fitted to the page. */
export interface LineFitOptions {
  /** A system's width (staff spaces) at a size in px per staff space. */
  widthAt: (spaceSize: number) => number;
  /** The size the reader chose: the fit never draws larger. */
  spaceSize: number;
  /** The smallest it draws; a line still too wide splits in half. */
  floorSpaceSize: number;
}

export interface LineFit {
  /** px per staff space: the reader's size, or smaller so the lines fit. */
  spaceSize: number;
  /** The system width at that size, in staff spaces. */
  systemWidth: number;
  lines: PlannedLine[];
}

/**
 * Plan lines of `perLine` bars and fit them to the page. A line that cannot
 * fit even at the floor splits in half. When most full lines must, the whole
 * tune is planned again at half the count (a phone's two to a line), so the
 * short lines, endings, the pickup and a sparse line that would still have
 * fitted all sit on the one new grid; otherwise only the lines that need it
 * split, as often as it takes, down to a single bar, which may then overflow
 * as any too-wide bar does. The staff then draws at the
 * largest size, up to the reader's own, at which every line that can fit
 * does. `widths` are the bars' natural widths in staff spaces; the first line
 * also holds the header indent (the time signature).
 */
export function fitLines(
  bars: readonly LineBar[],
  perLine: number,
  widths: readonly number[],
  firstLineIndent: number,
  options: LineFitOptions,
): LineFit {
  const floor = Math.min(options.floorSpaceSize, options.spaceSize);
  const required = (line: PlannedLine) => {
    let width = line.start === 0 ? firstLineIndent : 0;
    for (let i = line.start; i < line.end; i++) width += widths[i] ?? 0;
    return width;
  };
  const fitsAt = (line: PlannedLine, spaceSize: number) =>
    required(line) <= options.widthAt(spaceSize) + 1e-6;
  const pickup = bars[0]?.pickup ?? false;
  const split = (line: PlannedLine): PlannedLine[] => {
    const lead = line.start === 0 && pickup ? 1 : 0;
    const full = line.end - line.start - lead;
    if (full < 2) return [line];
    const middle = line.start + lead + Math.ceil(full / 2);
    return [
      { start: line.start, end: middle, fill: 1 },
      { start: middle, end: line.end, fill: 1 },
    ];
  };
  let per = Math.max(1, Math.floor(perLine));
  let planned = planLines(bars, per);
  for (;;) {
    const full = planned.filter((line) => line.fill === 1);
    const tooWide = full.filter((line) => !fitsAt(line, floor)).length;
    if (per > 1 && tooWide * 2 > full.length) {
      per = Math.ceil(per / 2);
      planned = planLines(bars, per);
      continue;
    }
    const next = planned.flatMap((line) => (fitsAt(line, floor) ? [line] : split(line)));
    if (next.length === planned.length) break;
    planned = next;
  }
  // A single bar too wide even at the floor overflows whatever the size, so it
  // does not hold the rest of the tune down at the floor.
  const fitting = planned.filter((line) => fitsAt(line, floor));
  const fitsEverything = (spaceSize: number) => fitting.every((line) => fitsAt(line, spaceSize));
  let spaceSize = options.spaceSize;
  if (!fitsEverything(spaceSize)) {
    let low = floor;
    let high = options.spaceSize;
    for (let step = 0; step < 40; step++) {
      const middle = (low + high) / 2;
      if (fitsEverything(middle)) low = middle;
      else high = middle;
    }
    spaceSize = low;
  }
  return { spaceSize, systemWidth: options.widthAt(spaceSize), lines: planned };
}

/**
 * The reading view's fit (`fitLines` at the default size, scaled by the
 * reader's). The lines are fitted at `baseSpaceSize`, the device's 100%, with
 * the floor at LINE_FIT_FLOOR of it; the staff then draws at the reader's
 * `spaceSize` times the share that fit took, and the lines are planned again at
 * that size, splitting any the page no longer holds. So the size is always in
 * proportion to the slider: at 100% the fitted default, smaller below it (where
 * lines that split at the default may join up again), and above it bigger,
 * with lines splitting rather than the size standing still.
 */
export function scaledLineFit(
  bars: readonly LineBar[],
  perLine: number,
  widths: readonly number[],
  firstLineIndent: number,
  options: {
    widthAt: (spaceSize: number) => number;
    /** The size the reader chose. */
    spaceSize: number;
    /** The device's default size (100%), where the fit is made. */
    baseSpaceSize: number;
  },
): LineFit {
  const { widthAt, baseSpaceSize } = options;
  const atDefault = fitLines(bars, perLine, widths, firstLineIndent, {
    widthAt,
    spaceSize: baseSpaceSize,
    floorSpaceSize: baseSpaceSize * LINE_FIT_FLOOR,
  });
  const spaceSize = options.spaceSize * (atDefault.spaceSize / baseSpaceSize);
  return fitLines(bars, perLine, widths, firstLineIndent, {
    widthAt,
    spaceSize,
    floorSpaceSize: spaceSize,
  });
}
